/* ═══════════════════════════════════════════════════════
   APEXFPV Wishlist — localStorage engine
   Storage: apexfpv_wishlist = [{variantId, productId, handle,
            title, image, price, addedAt}]
   API:
     ApexWishlist.list()                  -> array
     ApexWishlist.has(variantId)          -> bool
     ApexWishlist.add(item)               -> array (after add)
     ApexWishlist.remove(variantId)       -> array
     ApexWishlist.removeMany(arrayIds)    -> array
     ApexWishlist.clear()                 -> []
     ApexWishlist.count()                 -> number
     ApexWishlist.fetchStock(handle)      -> Promise<productJson>
     ApexWishlist.transfer(variantIds)    -> Promise<{ok,failed}>
   Events dispatched on document:
     apex:wishlist:change   (after any mutation)
     apex:wishlist:add      (item just added)
   ═══════════════════════════════════════════════════════ */
(function (window, document) {
  'use strict';

  var KEY = 'apexfpv_wishlist';
  var stockCache = {}; /* handle -> productJson */

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); }
    catch (e) { /* quota */ }
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (e) {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, false, false, detail || {});
      document.dispatchEvent(ev);
    }
  }

  function refreshBadges() {
    var n = read().length;
    document.querySelectorAll('[data-apex-wish-badge]').forEach(function (el) {
      if (n > 0) {
        el.textContent = n;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  function refreshButtons() {
    var ids = read().map(function (x) { return String(x.variantId); });
    document.querySelectorAll('[data-apex-wish-btn]').forEach(function (btn) {
      var vid = btn.getAttribute('data-variant-id');
      if (!vid) return;
      var inList = ids.indexOf(String(vid)) !== -1;
      btn.classList.toggle('is-in-wishlist', inList);
      var label = btn.querySelector('[data-wish-label]');
      if (label) label.textContent = inList ? 'In your wishlist' : 'Save to wishlist';
    });
  }

  function list() { return read(); }

  function has(variantId) {
    var v = String(variantId);
    return read().some(function (x) { return String(x.variantId) === v; });
  }

  function add(item) {
    if (!item || !item.variantId) return read();
    var arr = read();
    var v = String(item.variantId);
    if (arr.some(function (x) { return String(x.variantId) === v; })) {
      return arr; /* dedupe */
    }
    var entry = {
      variantId: item.variantId,
      productId: item.productId || null,
      handle:    item.handle || '',
      title:     item.title || '',
      image:     item.image || '',
      price:     item.price || 0,
      addedAt:   Date.now()
    };
    arr.push(entry);
    write(arr);
    refreshBadges();
    refreshButtons();
    emit('apex:wishlist:add', { item: entry, count: arr.length });
    emit('apex:wishlist:change', { count: arr.length });
    return arr;
  }

  function remove(variantId) {
    var v = String(variantId);
    var arr = read().filter(function (x) { return String(x.variantId) !== v; });
    write(arr);
    refreshBadges();
    refreshButtons();
    emit('apex:wishlist:change', { count: arr.length });
    return arr;
  }

  function removeMany(ids) {
    var set = {};
    (ids || []).forEach(function (i) { set[String(i)] = 1; });
    var arr = read().filter(function (x) { return !set[String(x.variantId)]; });
    write(arr);
    refreshBadges();
    refreshButtons();
    emit('apex:wishlist:change', { count: arr.length });
    return arr;
  }

  function clear() {
    write([]);
    refreshBadges();
    refreshButtons();
    emit('apex:wishlist:change', { count: 0 });
    return [];
  }

  function count() { return read().length; }

  /* Fetch product JSON to know current variant inventory + availability.
     Cached per handle for the page lifetime. */
  function fetchStock(handle) {
    if (!handle) return Promise.resolve(null);
    if (stockCache[handle]) return Promise.resolve(stockCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (data) stockCache[handle] = data;
      return data;
    })
    .catch(function () { return null; });
  }

  /* Look up a single variant inside fetched product json.
     Returns { available, max, tracked } */
  function variantStock(productJson, variantId) {
    if (!productJson) return { available: true, max: 99, tracked: false };
    var v = (productJson.variants || []).find(function (x) {
      return String(x.id) === String(variantId);
    });
    if (!v) return { available: false, max: 0, tracked: true };
    var tracked = (v.inventory_management != null && v.inventory_management !== '' &&
                   v.inventory_policy === 'deny');
    var max = tracked ? (typeof v.inventory_quantity === 'number'
                          ? Math.max(0, v.inventory_quantity) : 0)
                      : 99;
    var available = v.available !== false && (!tracked || max > 0);
    return { available: available, max: max, tracked: tracked };
  }

  /* Transfer items to cart in one batched POST /cart/add.js
     Skips OOS items. Returns { added, skipped } */
  function transfer(variantIds) {
    var ids = (variantIds || []).map(String);
    if (!ids.length) return Promise.resolve({ added: [], skipped: [] });
    var items = read().filter(function (x) { return ids.indexOf(String(x.variantId)) !== -1; });
    var uniqueHandles = {};
    items.forEach(function (i) { if (i.handle) uniqueHandles[i.handle] = 1; });
    var handleList = Object.keys(uniqueHandles);

    return Promise.all(handleList.map(function (h) { return fetchStock(h); }))
      .then(function () {
        var addList = [];
        var skipped = [];
        items.forEach(function (it) {
          var prod = stockCache[it.handle];
          var st = variantStock(prod, it.variantId);
          if (st.available && st.max > 0) {
            addList.push({ id: it.variantId, quantity: 1 });
          } else {
            skipped.push(it);
          }
        });
        if (!addList.length) return { added: [], skipped: skipped };
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: addList })
        })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (resp) {
          if (!resp.ok) {
            return { added: [], skipped: skipped, error: resp.body };
          }
          /* Remove successfully added items from wishlist */
          var addedIds = addList.map(function (x) { return x.id; });
          removeMany(addedIds);
          return { added: addedIds, skipped: skipped };
        });
      });
  }

  /* Toggle helper used by PDP button */
  function toggle(item) {
    if (has(item.variantId)) {
      remove(item.variantId);
      return false;
    }
    add(item);
    return true;
  }

  /* ── Init: wire up buttons + cross-tab sync ── */
  function init() {
    refreshBadges();
    refreshButtons();
  }

  /* Cross-tab sync */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) {
      refreshBadges();
      refreshButtons();
      emit('apex:wishlist:change', { count: read().length });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ApexWishlist = {
    list: list, has: has, add: add, remove: remove,
    removeMany: removeMany, clear: clear, count: count,
    toggle: toggle, fetchStock: fetchStock, variantStock: variantStock,
    transfer: transfer, refreshBadges: refreshBadges, refreshButtons: refreshButtons
  };

})(window, document);
